/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { DatePipe } from '@angular/common';
import { inject, Pipe, PipeTransform } from '@angular/core';

/** Buddhist Era runs 543 years ahead of the Common Era. */
export const BUDDHIST_ERA_OFFSET = 543;

/**
 * Converts a Common Era year to its Buddhist Era equivalent.
 *
 * Exported so date **inputs**, which are formatted by the browser and not by
 * this pipe, can show the same year the rest of the UI does.
 */
export function toBuddhistYear(year: number): number {
  return year + BUDDHIST_ERA_OFFSET;
}

/**
 * Formats a date the way the product reads it: Thai month names from the
 * application locale, and a **Buddhist Era** year.
 *
 * Drop-in replacement for `| date`, taking the same arguments:
 *
 * ```html
 * {{ node.modifiedAt | acaDate: 'd MMM y HH:mm' }}
 * ```
 *
 * ### Why the year is substituted rather than the date shifted
 *
 * Adding 543 years to the `Date` itself would be simpler but wrong on 29
 * February: 2024 is a leap year and 2567 is not, so the shifted date silently
 * becomes 1 March. Instead the date is formatted normally and only the rendered
 * year is replaced.
 *
 * The year is located by formatting it on its own and matching that exact run of
 * digits, bounded by non-digits, so a time can never be mistaken for it — a
 * formatted time has no unseparated four-digit run. If the year cannot be found
 * in the output (an unusual format, a locale that spells it out), the formatted
 * string is returned untouched: a date that reads in the wrong era is a smaller
 * failure than one mangled by a bad substitution.
 */
@Pipe({ name: 'acaDate', standalone: true })
export class ThaiDatePipe implements PipeTransform {
  private readonly datePipe = inject(DatePipe);

  transform(value: Date | string | number | null | undefined, format = 'mediumDate', timezone?: string, locale?: string): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const formatted = this.datePipe.transform(value, format, timezone, locale);
    if (!formatted) {
      return formatted;
    }

    const commonEraYear = this.datePipe.transform(value, 'y', timezone, locale);
    if (!commonEraYear) {
      return formatted;
    }

    return withBuddhistYear(formatted, commonEraYear);
  }
}

/**
 * Replaces the Common Era year in an already formatted date with its Buddhist
 * Era equivalent, using the rule `ThaiDatePipe` documents: the year is matched as
 * an exact run of digits bounded by non-digits, and a string that does not
 * contain it is returned untouched.
 *
 * Exported for formatters the pipe cannot reach — ADF's metadata card and the
 * date picker's adapter both format dates themselves.
 */
export function withBuddhistYear(formatted: string, commonEraYear: number | string): string {
  const year = String(commonEraYear);
  if (!formatted || !/^\d+$/.test(year)) {
    return formatted;
  }

  const buddhistYear = String(toBuddhistYear(Number(year)));
  const yearPattern = new RegExp(`(^|\\D)${year}(\\D|$)`);

  return yearPattern.test(formatted) ? formatted.replace(yearPattern, `$1${buddhistYear}$2`) : formatted;
}
